import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, MoreHorizontalIcon } from 'lucide-react';
import { EmptyState } from './EmptyState';
export interface ColumnDef<T> {
  header: string;
  accessorKey?: keyof T;
  cell?: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}
interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}
export function DataTable<T extends {
  id?: string | number;
}>({
  data,
  columns,
  onRowClick,
  isLoading,
  emptyState,
  className = ''
}: DataTableProps<T>) {
  if (isLoading) {
    return <div className="w-full animate-pulse space-y-4" data-id="element-171">
        <div className="h-10 bg-surface-secondary rounded w-full" data-id="element-172"></div>
        {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-surface-secondary rounded w-full" data-id="element-173"></div>)}
      </div>;
  }
  if (data.length === 0) {
    return <div className="w-full border border-border-light rounded-fluent-lg bg-surface-primary" data-id="element-174">
        {emptyState || <EmptyState icon={<MoreHorizontalIcon data-id="element-176" />} title="No data available" description="There are no records to display at this time." data-id="element-175" />}
      </div>;
  }
  return <div className={`w-full overflow-x-auto border border-border-light rounded-fluent-lg bg-surface-primary ${className}`} data-id="element-177">
      <table className="w-full text-left border-collapse" data-id="element-178">
        <thead data-id="element-179">
          <tr className="bg-surface-secondary border-b border-border-light" data-id="element-180">
            {columns.map((col, i) => <th key={i} className={`px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider ${col.width ? `w-[${col.width}]` : ''} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`} data-id="element-181">
                <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'}`} data-id="element-182">
                  {col.header}
                  {col.sortable && <ChevronDownIcon className="w-3 h-3 text-text-tertiary" data-id="element-183" />}
                </div>
              </th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light" data-id="element-184">
          {data.map((row, rowIndex) => <tr key={row.id || rowIndex} onClick={() => onRowClick && onRowClick(row)} className={`group transition-colors ${onRowClick ? 'cursor-pointer hover:bg-surface-secondary' : ''}`} data-id="element-185">
              {columns.map((col, colIndex) => <td key={colIndex} className={`px-4 py-3 text-sm text-text-primary whitespace-nowrap ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`} data-id="element-186">
                  {col.cell ? col.cell(row) : col.accessorKey ? String(row[col.accessorKey]) : null}
                </td>)}
            </tr>)}
        </tbody>
      </table>
    </div>;
}